import React, { useState, useEffect, useCallback } from "react";
import {
  Col,
  Button,
  Row,
  Tag,
  Tooltip,
  Space,
  Input,
  Form,
  DatePicker,
  Avatar,
} from "antd";
import {
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  VideoCameraOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { StyledTable } from "../../admin/videos/styled";
import { patientService } from "../../../../services/user";
import { notify, debounce } from "../../../../utils/helper";
import { capitalizeFirstLetter } from "../../../../utils/utils";
import moment from "moment";
import { CustomSearchContainer } from "../../../table-item";
import { FiSearch } from "react-icons/fi";
import { extractToken } from "../../../../utils/localStorage";
import { globalRoles } from "../../../../utils/roles";
import AddPatientModal from "../../patients_page/AddPatientModal";
import DeleteConfirmationModal from "../../physician_page/component/DeleteConfirmationModal";
import PhysicianDetailModal from "../../physician_page/component/PhysicianDetailModal";
import AssignVideoToPatientModal from "../../patients_page/AssignVideoToPatientModal";
import AddPhysicianModalWrapper from "../../../modal/AddPhysicianModalWrapper";

// Schedule Appointment Modal Component
const ScheduleAppointmentModal = ({
  isOpen,
  setOpen,
  patientId,
  patientName,
}) => {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleSave = async () => {
    try {
      setConfirmLoading(true);
      const values = await form.validateFields();

      // Here you would save the appointment data to your backend
      // For example: await appointmentService.create({ patientId, date: values.appointmentDate })

      notify("success", "Appointment scheduled successfully");
      setOpen(false);
    } catch (error) {
      console.error("Error scheduling appointment:", error);
      notify("error", "Failed to schedule appointment");
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <AddPhysicianModalWrapper
      title={
        <div className="row-flex flex-column">
          <h3 className="font-18 font-semibold text-primary mb-5">
            Schedule Appointment
          </h3>
        </div>
      }
      show={isOpen}
      setShow={() => setOpen(false)}
      confirmLoading={confirmLoading}
      footer={null}
      keyClose={false}
      className="custom-detail-modal"
    >
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Form.Item label={`Schedule an appointment for ${patientName}`}>
          <p className="text-primary">Select a date and time for the appointment:</p>
        </Form.Item>
        <Form.Item
          name="appointmentDate"
          rules={[
            {
              required: true,
              message: "Please select appointment date and time",
            },
          ]}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            style={{ width: "100%" }}
            disabledDate={(current) =>
              current && current < moment().startOf("day")
            }
          />
        </Form.Item>

        <Row>
          <Col span={24} className="d-flex justify-end gap-12">
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save Appointment
            </Button>
          </Col>
        </Row>
      </Form>
    </AddPhysicianModalWrapper>
  );
};

const CCMCoordinatorPatients = ({ refreshTrigger, physicianList }) => {
  const user = extractToken();
  const isCoordinator = user?.role === globalRoles.CCM_COORDINATOR;
  const isManager = user?.role === globalRoles.CCM_MANAGER;

  const [allpatient, setAllpatient] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(false);
  const [isViewDetail, setIsViewDetail] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [patientDataById, setPatientDataById] = useState([]);
  const [patientEdit, setPatientEdit] = useState(false);
  const [patientId, setPatientId] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [count, setCount] = useState(0);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [selectedPatientForScheduling, setSelectedPatientForScheduling] =
    useState(null);
  const [selectedPatientName, setSelectedPatientName] = useState("");
  const [searchText, setSearchText] = useState("");
  const [sortingColumn, setSortingColumn] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isAssignVideoModalOpen, setIsAssignVideoModalOpen] = useState(false);
  const [isModalOpen, setisModalOpen] = useState(false);

  const fetchPatient = async (searchInput = searchText) => {
    try {
      setLoading(true);
      const response = await patientService.getAll(
        pagination?.pageSize,
        pagination?.current,
        searchInput,
        sortingColumn, 
        "",
        user?.role === globalRoles.SUPERADMIN ? "true" : null
      );
      const processedData = processData(response?.data?.results || []);
      setAllpatient(processedData);
      setCount(response?.data?.totalResults);
    } catch (error) {
      console.error("Error fetching patient:", error);
    } finally {
      setLoading(false);
    }
  };

  const processData = (data) => {
    return data.map((item) => ({
      ...item,
      nameText: item?.name,
      name: (
        <>
          <div className="row-flex">
            <div className="d-flex justify-content-center">
              <Avatar
                size={40}
                style={{
                  marginRight: "8px",
                  backgroundColor: "var(--border-color)",
                  color: "var(--primary-text-color)",
                }}
              >
                {capitalizeFirstLetter(item?.name?.[0])}
              </Avatar>
            </div>
            <div className="d-flex flex-column justify-start align-start">
              <span>{capitalizeFirstLetter(item?.name)}</span>
              <span>{capitalizeFirstLetter(item?.email)}</span>
            </div>
          </div>
        </>
      ),
      gender: item.gender ? capitalizeFirstLetter(item.gender) : "-",
      statusValue: item.status,
      key: item.id,
    }));
  };

  useEffect(() => {
    fetchPatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sortingColumn, refreshTrigger]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      await patientService.delete(selectedId);
      fetchPatient();
      setIsDeleteModalOpen(false);
      notify("success", "Patient Delete Successfully.");
      setLoading(false);
    } catch (error) {
      console.error("Error deleting patient:", error);
      setLoading(false);
    }
  };

  const getPatientById = async () => {
    try {
      const res = await patientService.getById(selectedPatientId);
      setPatientDataById(res?.data);
    } catch (err) {}
  };

  useEffect(() => {
    if (selectedPatientId) {
      getPatientById();
    }
  }, [selectedPatientId]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleAssignVideoClick = () => {
    setIsAssignVideoModalOpen(true);
  };

  const handlePagination = (page) => {
    setPagination((prev) => ({ ...prev, current: page }));
  };

  const handleSearch = useCallback(
    debounce((value) => {
      fetchPatient(value);
    }, 500),
    []
  );

  const handleChangeTable = (a, b, c) => {
    const sortColumn = c?.column?.dataIndex;
    const sortOrder = c?.order;

    if (sortColumn && sortOrder) {
      const normalizedOrder = sortOrder === "ascend" ? "asc" : "desc";
      setSortingColumn(`${sortColumn}:${normalizedOrder}`);
    } else {
      setSortingColumn("");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "fullName",
      width: 350,
      sorter: (a, b) => (a.fullName || "").localeCompare(b.fullName || ""),
    },
    {
      title: "Coordinator",
      dataIndex: "ccm",
      width: 150,
      key: "ccm",
      sorter: (a, b) => (a.createdBy || "").localeCompare(b.createdBy || ""),
      render: (parent) => (
        <>
          {parent ? (
            parent?.map((item) => (
              <div className="row-flex">
                <div className="d-flex justify-content-center">
                  <Avatar
                    size={40}
                    style={{
                      marginRight: "8px",
                      backgroundColor: "var(--border-color)",
                      color: "var(--primary-text-color)",
                    }}
                  >
                    {capitalizeFirstLetter(item?.name?.[0])}
                  </Avatar>
                </div>
                <div className="d-flex flex-column justify-start align-start">
                  <span>{capitalizeFirstLetter(item.name)}</span>
                  <span>{capitalizeFirstLetter(item.email)}</span>
                </div>
              </div>
            ))
          ) : (
            <div>{"N/A"}</div>
          )}
        </>
      ),
      hidden: isCoordinator || isManager,
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      key: "status1",
      sorter: (a, b) => (a.status1 || "").localeCompare(b.status1 || ""),
      render: (status) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      align: "center",
      key: "createdAt",
      width: 180,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (date) =>
        date ? moment(date).format("DD MMM YYYY hh:mm A") : "N/A",
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      align: "center",
      key: "updatedAt",
      width: 180,
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
      render: (date) =>
      date ? moment(date).format("DD MMM YYYY hh:mm A") : "N/A",
    },
    {
      title: "Actions",
      align: "center",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Details">
            <div
              onClick={() => {
                setSelectedPatientId(record?.id);
                setIsViewDetail(true);
              }}
              className="icon-button-view"
            >
              <EyeOutlined />
            </div>
          </Tooltip>

          {/* <Tooltip title="Schedule Appointment">
            <div
              onClick={() => {
                setSelectedPatientForScheduling(record?.id);
                setSelectedPatientName(record?.name);
                setIsCalendarModalOpen(true);
              }}
              className=""
            >
              <CalendarOutlined />
            </div>
          </Tooltip> */}

          {(user?.role === globalRoles.SUPERADMIN || isCoordinator || isManager) && (
            <>
              <Tooltip title="Edit">
                <div
                  onClick={() => {
                    setPatientEdit(true);
                    setPatientId(record);
                    setisModalOpen(true);
                  }}
                  className="icon-button-edit"
                >
                  <EditOutlined />
                </div>
              </Tooltip>
              <Tooltip title="Delete">
                <div
                  onClick={() => {
                    setSelectedId(record?.id);
                    setIsDeleteModalOpen(true);
                  }}
                  className="icon-button-delete"
                >
                  <DeleteOutlined />
                </div>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const hasData = allpatient.length > 0;
  const scroll = hasData.length ? { y: `calc(100vh - 320px)`, x: true } : null;

  return (
    <>
      {isModalOpen && (
        <AddPatientModal
          isOpen={isModalOpen}
          setOpen={setisModalOpen}
          isEdit={patientEdit}
          setEdit={setPatientEdit}
          patientId={patientId}
          fetchPatient={fetchPatient}
          physicianList={physicianList}
          userDetails={user}
          ccm={true}
        />
      )}

      {isViewDetail && (
        <PhysicianDetailModal
          isOpen={isViewDetail}
          setOpen={setIsViewDetail}
          DataById={patientDataById}
          title={"Patient Details"}
          subtitle={"View the details of the patient."}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          onConfiem={() => handleDelete()}
          loading={loading}
        />
      )}

      {isAssignVideoModalOpen && (
        <AssignVideoToPatientModal
          isOpen={isAssignVideoModalOpen}
          setOpen={setIsAssignVideoModalOpen}
          selectedPatientIds={selectedRowKeys}
          onSuccess={() => {
            setSelectedRowKeys([]);
            notify("success", "Video assigned successfully");
            fetchPatient();
          }}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      )}

      {isCalendarModalOpen && (
        <ScheduleAppointmentModal
          isOpen={isCalendarModalOpen}
          setOpen={setIsCalendarModalOpen}
          patientId={selectedPatientForScheduling}
          patientName={selectedPatientName}
        />
      )}

      <Row style={{ padding: "0px 0px" }}>
        <Col
          span={24}
          className="d-flex"
          style={{ justifyContent: "space-between"}}
        >
          <div style={{ width: "100%" }}>
            <CustomSearchContainer>
              <FiSearch
                style={{ position: "absolute", zIndex: 1 }}
                className="icon ml-5"
              />
              <Input
                placeholder="Search Patients..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  handleSearch(e.target.value);
                }}
              />
            </CustomSearchContainer>
          </div>
        </Col>

        <Col span={24} style={{ flex: 1, padding: "24px 0px" }}>
          <StyledTable
            loading={loading}
            // rowSelection={rowSelection}
            columns={columns.filter((col) => !col.hidden)}
            dataSource={allpatient}
            rowKey="id"
            scroll={scroll}
            pagination={{
              current: pagination?.current,
              pageSize: pagination?.pageSize,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100],
              onShowSizeChange: (current, size) => {
                setPagination({
                  ...pagination,
                  current,
                  pageSize: size,
                });
              },
              total: count,
              showTotal: (total, range) =>
                `${range[0]} - ${range[1]} of ${total} patients`,
              showQuickJumper: false,
              showLessItems: true,
              onChange: handlePagination,
              itemRender: (page, type, originalElement) => {
                if (type === "prev") {
                  return (
                    <Button
                      type="text"
                      disabled={pagination?.current === 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pagination?.current > 1) {
                          setPagination((prev) => ({
                            ...prev,
                            current: prev.current - 1,
                          }));
                        }
                      }}
                    >
                      Previous
                    </Button>
                  );
                }
                if (type === "next") {
                  const maxPage = Math.ceil(count / pagination?.pageSize);
                  return (
                    <Button
                      type="text"
                      disabled={pagination?.current === maxPage}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pagination?.current < maxPage) {
                          setPagination((prev) => ({
                            ...prev,
                            current: prev.current + 1,
                          }));
                        }
                      }}
                    >
                      Next
                    </Button>
                  );
                }
                return originalElement;
              },
            }}
            onChange={handleChangeTable}
          />
        </Col>
      </Row>
    </>
  );
};

export default CCMCoordinatorPatients;
